import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API, PHOTO_URL } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlassIcon, FunnelIcon, StarIcon, PencilSimpleIcon, TrashIcon, EyeIcon, PrinterIcon, CaretUpIcon, CaretDownIcon, BookmarkSimpleIcon } from "@phosphor-icons/react";
import { CargrListingDialog } from "@/components/CargrListingDialog";
import { CargrSyncReportDialog } from "@/components/CargrSyncReportDialog";
import { SYNC_BOOKMARKLET_TEMPLATE } from "@/lib/cargrBookmarklet";

const fmtEUR = (n) =>
  new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
};

export default function Inventory() {
  const [cars, setCars] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("unsold");
  const [sort, setSort] = useState({ key: "", dir: 1 });
  const [loading, setLoading] = useState(true);
  const [cargrDialogCar, setCargrDialogCar] = useState(null);
  const [syncReport, setSyncReport] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const syncBookmarkletUri = useMemo(() => {
    const compact = SYNC_BOOKMARKLET_TEMPLATE(window.location.origin)
      .replace(/\n\s*/g, " ").replace(/\s+/g, " ").trim();
    // eslint-disable-next-line no-script-url
    return "javascript:" + encodeURIComponent(compact);
  }, []);

  const onSyncDragStart = (e) => {
    try {
      e.dataTransfer.effectAllowed = "copy";
      e.dataTransfer.setData("text/uri-list", syncBookmarkletUri);
      e.dataTransfer.setData("text/plain", syncBookmarkletUri);
      e.dataTransfer.setData("text/x-moz-url", `${syncBookmarkletUri}\nΣυγχρονισμός αγγελιών car.gr → BESIKOS`);
    } catch { /* ignore */ }
  };

  const onSyncClick = () => {
    toast.info("Μην το πατάτε εδώ — σύρετέ το με το ποντίκι στη γραμμή σελιδοδεικτών του browser (Ctrl+Shift+B για εμφάνιση). Μετά ανοίξτε το besikoscars.car.gr και πατήστε το bookmark.", { duration: 8000 });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const r = await api.get("/cars", { params });
    setCars(r.data);
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => {
    load();
  }, [statusFilter, load]);

  useEffect(() => {
    const tok = searchParams.get("cargr_sync");
    if (!tok) return;
    api.get(`/cargr/sync-report/${tok}`)
      .then((r) => setSyncReport(r.data))
      .catch(() => toast.error("Η αναφορά συγχρονισμού δεν βρέθηκε ή έληξε"));
  }, [searchParams]);

  const closeSyncReport = () => {
    setSyncReport(null);
    searchParams.delete("cargr_sync");
    setSearchParams(searchParams, { replace: true });
    load();
  };

  const onCargrSaved = (id, lid) =>
    setCars((cur) => cur.map((c) => (c.id === id ? { ...c, cargr_listing_id: lid } : c)));

  const toggleVisibility = async (id, channel, next) => {
    // optimistic update
    setCars((cur) => cur.map((c) => (c.id === id ? {
      ...c,
      include_in_wp_sync: channel === "site" ? next : c.include_in_wp_sync,
      include_in_car_gr_export: channel === "car_gr" ? next : c.include_in_car_gr_export,
    } : c)));
    try {
      const body = channel === "site" ? { site: next } : { car_gr: next };
      const r = await api.patch(`/cars/${id}/visibility`, body);
      if (channel === "site") {
        if (next) {
          if (r.data?.wp_sync_triggered) toast.success("✓ Στάλθηκε άμεσος συγχρονισμός στο WordPress");
          else toast.success("Ορατό στο site (sync εντός 1 ώρας μέσω WP cron)");
        } else {
          if (r.data?.wp_sync_triggered) toast.success("✓ Αποκρύφθηκε από το site (draft)");
          else toast.success("Αφαιρέθηκε από το site (θα αποκρυφθεί εντός 1 ώρας μέσω WP cron)");
        }
      }
    } catch {
      toast.error("Αποτυχία ενημέρωσης ορατότητας");
      load();
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Μετακίνηση στα διαγραμμένα;\n\nΘα μπορείτε να το επαναφέρετε από τη σελίδα «Διαγραμμένα».")) return;
    await api.delete(`/cars/${id}`);
    toast.success("Μετακινήθηκε στα διαγραμμένα");
    load();
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  };

  const sortedCars = useMemo(() => {
    if (!sort.key) return cars;
    const val = (c) => {
      switch (sort.key) {
        case "vehicle": return `${c.brand || ""} ${c.model || ""}`.toLowerCase();
        case "year": return c.year || 0;
        case "mileage": return c.mileage_km || 0;
        case "fuel": return (c.fuel_type || "").toLowerCase();
        case "transmission": return (c.transmission || "").toLowerCase();
        case "status": return ({ available: 0, reserved: 1, sold: 2 }[c.status] ?? 3);
        case "price": return c.price_eur || 0;
        case "photos": return (c.photos || []).length;
        case "cargr": return c.cargr_listing_id || "";
        case "created": return c.created_at || "";
        default: return 0;
      }
    };
    return [...cars].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -sort.dir;
      if (va > vb) return sort.dir;
      return 0;
    });
  }, [cars, sort]);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Filters */}
      <div className="border border-black/10 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
          <div className="md:col-span-7 border-r border-black/10 p-4 flex items-center gap-3">
            <MagnifyingGlassIcon size={20} color="#4B5563" />
            <input
              data-testid="inventory-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Αναζήτηση: μάρκα, μοντέλο, VIN, πινακίδα..."
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#9CA3AF]"
            />
            <button onClick={load} className="btn-primary text-xs py-2 px-4" data-testid="search-btn">
              Αναζήτηση
            </button>
          </div>
          <div className="md:col-span-3 border-r border-black/10 p-4 flex items-center gap-3">
            <FunnelIcon size={18} color="#4B5563" />
            <select
              data-testid="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
            >
              <option value="unsold">Απούλητα (διαθέσιμα + κρατημένα)</option>
              <option value="">Όλα</option>
              <option value="available">Διαθέσιμα</option>
              <option value="reserved">Κρατημένα</option>
              <option value="sold">Πωλημένα</option>
            </select>
          </div>
          <div className="md:col-span-2 p-4 flex items-center justify-end gap-2">
            <button
              type="button"
              draggable="true"
              onDragStart={onSyncDragStart}
              onClick={onSyncClick}
              className="border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white text-xs py-2 px-3 inline-flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none"
              title="Σύρετε αυτό το κουμπί στη γραμμή σελιδοδεικτών του browser. Μετά ανοίξτε το besikoscars.car.gr και πατήστε το bookmark για συγχρονισμό αριθμών αγγελιών."
              data-testid="drag-sync-bookmarklet"
            >
              <BookmarkSimpleIcon size={14} weight="fill" /> car.gr Sync
            </button>
            <Link to="/cars/new" className="btn-accent text-xs py-2 px-4" data-testid="inv-add-car">
              + Νέο
            </Link>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9FAFB] border-b-2 border-black/20">
              {[
                { label: "" },
                { label: "" },
                { label: "Όχημα", key: "vehicle" },
                { label: "Έτος", key: "year" },
                { label: "Χλμ.", key: "mileage" },
                { label: "Καύσιμο", key: "fuel" },
                { label: "Κιβ.", key: "transmission" },
                { label: "VIN" },
                { label: "Κατάσταση", key: "status" },
                { label: "EN" },
                { label: "Site" },
                { label: "car.gr" },
                { label: "Τιμή", key: "price" },
                { label: "Φωτο", key: "photos" },
                { label: "car.gr", key: "cargr" },
                { label: "Καταχ.", key: "created" },
                { label: "Ενέργειες" },
              ].map((h, i) => (
                <th
                  key={i}
                  onClick={h.key ? () => toggleSort(h.key) : undefined}
                  data-testid={h.key ? `sort-${h.key}` : undefined}
                  className={`text-left px-3 py-3 text-[10px] uppercase tracking-[0.1em] text-[#4B5563] font-medium whitespace-nowrap ${h.key ? "cursor-pointer select-none hover:text-[#0A0A0A]" : ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {h.label}
                    {h.key && sort.key === h.key && (
                      sort.dir === 1 ? <CaretUpIcon size={11} weight="bold" /> : <CaretDownIcon size={11} weight="bold" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              if (loading) {
                return (
                  <tr>
                    <td colSpan={17} className="px-4 py-16 text-center text-[#4B5563]">Φόρτωση...</td>
                  </tr>
                );
              }
              if (cars.length === 0) {
                return (
                  <tr>
                    <td colSpan={17} className="px-4 py-16 text-center text-[#4B5563]">
                      Δεν βρέθηκαν αυτοκίνητα. <Link to="/cars/new" className="underline">Καταχώρηση</Link>
                    </td>
                  </tr>
                );
              }
              return sortedCars.map((c) => {
                const firstPhoto = (c.photos || [])[0];
                const thumb = firstPhoto ? PHOTO_URL(firstPhoto.url) : null;
                return (
                <tr key={c.id} className="border-b border-black/10 hover:bg-[#F9FAFB] transition-colors" data-testid={`car-row-${c.id}`}>
                  <td className="px-3 py-3">{c.featured ? <StarIcon size={14} weight="fill" color="#F59E0B" /> : null}</td>
                  <td className="px-3 py-3">
                    <Link to={`/cars/${c.id}`} className="block w-12 h-12 bg-[#F3F4F6] border border-black/10 overflow-hidden" data-testid={`thumb-${c.id}`}>
                      {thumb ? (
                        <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#9CA3AF] text-[10px]">—</div>
                      )}
                    </Link>
                  </td>
                  <td className="px-3 py-3">
                    <Link to={`/cars/${c.id}`} className="font-medium hover:text-[#E60000]">
                      {c.brand} {c.model}
                    </Link>
                    {c.variant && <div className="text-xs text-[#4B5563]">{c.variant}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono">{c.year}</td>
                  <td className="px-3 py-3 font-mono">{new Intl.NumberFormat("el-GR").format(c.mileage_km)}</td>
                  <td className="px-3 py-3">{c.fuel_type}</td>
                  <td className="px-3 py-3">{c.transmission}</td>
                  <td className="px-3 py-3 font-mono text-xs">{c.vin || "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-3"><TranslationBadge car={c} /></td>
                  <td className="px-3 py-3 text-center">
                    <VisibilityToggle
                      on={c.include_in_wp_sync !== false}
                      label="Site"
                      onChange={(next) => toggleVisibility(c.id, "site", next)}
                      testid={`toggle-site-${c.id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <VisibilityToggle
                      on={c.include_in_car_gr_export !== false}
                      label="car.gr"
                      onChange={(next) => toggleVisibility(c.id, "car_gr", next)}
                      testid={`toggle-cargr-${c.id}`}
                    />
                  </td>
                  <td className="px-3 py-3 font-mono font-medium">{fmtEUR(c.price_eur)}</td>
                  <td className="px-3 py-3 font-mono">{(c.photos || []).length}</td>
                  <td className="px-3 py-3 font-mono text-xs" data-testid={`cargr-id-${c.id}`}>
                    {c.cargr_listing_id
                      ? <span>{c.cargr_listing_id}</span>
                      : <span className="text-[#E60000] font-bold" title="Χωρίς αριθμό αγγελίας car.gr">—</span>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs" data-testid={`created-${c.id}`}>{fmtDate(c.created_at)}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      {c.include_in_wp_sync !== false ? (
                        <a href={`${API}/go/${c.id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#0055FF] hover:text-white" title="Προβολή στο site" data-testid={`preview-${c.id}`}>
                          <EyeIcon size={16} />
                        </a>
                      ) : (
                        <span className="p-1.5 text-black/20 cursor-not-allowed" title="Δεν εμφανίζεται στο site" data-testid={`preview-disabled-${c.id}`}>
                          <EyeIcon size={16} />
                        </span>
                      )}
                      {c.include_in_car_gr_export !== false && c.cargr_listing_id ? (
                        <a href={`https://www.car.gr/cars/view/${c.cargr_listing_id}`} target="_blank" rel="noreferrer" className="p-1.5 text-[#0055FF] hover:bg-[#0055FF] hover:text-white" title="Προβολή στο car.gr" data-testid={`cargr-view-${c.id}`}>
                          <EyeIcon size={16} weight="fill" />
                        </a>
                      ) : c.include_in_car_gr_export !== false ? (
                        <button onClick={() => setCargrDialogCar(c)} className="p-1.5 text-[#0055FF]/30 hover:text-[#0055FF] hover:bg-black/5" title="Λείπει ο αριθμός αγγελίας car.gr — πατήστε για συμπλήρωση" data-testid={`cargr-set-${c.id}`}>
                          <EyeIcon size={16} weight="fill" />
                        </button>
                      ) : (
                        <span className="p-1.5 text-black/15 cursor-not-allowed" title="Δεν εξάγεται στο car.gr" data-testid={`cargr-disabled-${c.id}`}>
                          <EyeIcon size={16} weight="fill" />
                        </span>
                      )}
                      <a
                        href={`${API}/cars/${c.id}/brochure.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 hover:bg-[#16A34A] hover:text-white"
                        title="Εκτύπωση A4"
                        data-testid={`brochure-${c.id}`}
                      >
                        <PrinterIcon size={16} />
                      </a>
                      <a
                        href={`${API}/cars/${c.id}/brochure.pdf?plain=1`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 text-[#E60000] hover:bg-[#E60000] hover:text-white"
                        title="Εκτύπωση A4 χωρίς τιμή & QR"
                        data-testid={`brochure-plain-${c.id}`}
                      >
                        <PrinterIcon size={16} weight="fill" />
                      </a>
                      <Link to={`/cars/${c.id}`} className="p-1.5 hover:bg-black/5" title="Επεξεργασία" data-testid={`edit-${c.id}`}>
                        <PencilSimpleIcon size={16} />
                      </Link>
                      <button onClick={() => onDelete(c.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white" title="Διαγραφή" data-testid={`delete-${c.id}`}>
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[#4B5563] font-mono">
        Σύνολο: {cars.length} εγγραφές
      </div>

      {cargrDialogCar && (
        <CargrListingDialog car={cargrDialogCar} onClose={() => setCargrDialogCar(null)} onSaved={onCargrSaved} />
      )}
      {syncReport && <CargrSyncReportDialog report={syncReport} onClose={closeSyncReport} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    available: { label: "Διαθέσιμο", bg: "bg-[#16A34A]" },
    reserved: { label: "Κρατημένο", bg: "bg-[#F59E0B]" },
    sold: { label: "Πωλήθηκε", bg: "bg-[#4B5563]" },
  };
  const s = map[status] || map.available;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider text-white ${s.bg}`}>
      <span className="w-1.5 h-1.5 bg-white" />
      {s.label}
    </span>
  );
}

function VisibilityToggle({ on, label, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!on);
      }}
      title={`${label}: ${on ? "Ενεργό" : "Ανενεργό"} (κλικ για εναλλαγή)`}
      data-testid={testid}
      className={`inline-flex items-center w-10 h-5 rounded-full transition-colors p-0.5 ${on ? "bg-[#16A34A]" : "bg-[#D1D5DB]"}`}
      aria-label={`${label}: ${on ? "Ενεργό" : "Ανενεργό"}`}
    >
      <span className={`block w-4 h-4 bg-white rounded-full shadow transform transition-transform ${on ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}


function TranslationBadge({ car }) {
  const hasEn = !!(car.description_en || (car.equipment_en && car.equipment_en.length));
  let status = car.translation_status || "no_english_version";
  if (!hasEn) status = "no_english_version";
  else if (status === "no_english_version") status = "ai_generated";
  const map = {
    no_english_version: { label: "—", title: "Χωρίς αγγλική έκδοση", cls: "bg-[#F3F4F6] text-[#9CA3AF]" },
    ai_generated: { label: "AI", title: "AI Generated", cls: "bg-[#FEF3C7] text-[#92400E]" },
    manually_reviewed: { label: "REV", title: "Manually reviewed", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
    approved: { label: "OK", title: "Approved", cls: "bg-[#D1FAE5] text-[#065F46]" },
  };
  const s = map[status] || map.no_english_version;
  return (
    <span
      className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-mono tracking-wider ${s.cls}`}
      title={s.title}
      data-testid={`translation-badge-${car.id}`}
    >
      {s.label}
    </span>
  );
}
